/*
 *   Copyright (c) 2022 
 *   All rights reserved.
 */

    // ============================================================
    // Google Maps result types that are NOT street addresses.
    // When these appear as the first segment of formatted_address,
    // the app must skip them and extract the real street address.
    // ============================================================
    const NON_ADDRESS_TYPES = [
        'establishment',
        'point_of_interest',
        'neighborhood',
        'premise',
        'subpremise',
        'natural_feature',
        'park',
        'airport',
        'bus_station',
        'train_station',
        'transit_station',
        'place_of_worship',
        'school',
        'university',
        'store',
        'restaurant',
        'cafe',
        'bar',
        'bank',
        'hospital',
        'pharmacy',
        'shopping_mall',
        'supermarket',
        'gas_station',
        'gym',
        'spa',
        'lodging',
        'parking',
        'campground',
        'fire_station',
        'police',
        'post_office',
        'library',
        'church',
        'hindu_temple',
        'mosque',
        'synagogue'
    ];

    /**
	 * Get the city and set the city input value to the one selected
	 *
	 * @param addressArray
	 * @return {string}
	 */
	const getCity = ( addressArray ) => {
		let city = '';
		for( let i = 0; i < addressArray.length; i++ ) {
			if ( addressArray[ i ].types[0] && addressArray[ i ].types.includes('locality') ) {
				city = addressArray[ i ].long_name;
				return city;
			}
		}
	};
    /**
	 * Get the city and set the city input value to the one selected
	 *
	 * @param addressArray
	 * @return {string}
	 */
	const getPostalCode = ( addressArray ) => {
		let postalCode = '';
		for( let i = 0; i < addressArray.length; i++ ) {
			if ( addressArray[ i ].types[0] && 'postal_code' === addressArray[ i ].types[0] ) {
				postalCode = addressArray[ i ].long_name;
				return postalCode;
			}
		}
	};
	/**
	 * Get the area and set the area input value to the one selected
	 *
	 * @param addressArray
	 * @return {string}
	 */
	const getArea = ( addressArray ) => {
		//const filteredStrings = ["sublocality_level_1","locality"]
		const filteredStrings = ["sublocality_level_1"]
		let area = '';
		for( let i = 0; i < filteredStrings.length; i++ ) {
			
				for ( let j = 0; j < addressArray.length; j++ ) {

					if ( addressArray[ j ].types[0] && addressArray[ j ].types.includes(filteredStrings[i])  ) {
						area += ", "+addressArray[ j ].long_name;
						
					}
				}
			
		}
		return area.replace(/^,/, '');
	};
	/**
	 * Get the address and set the address input value to the one selected
	 *
	 * @param addressArray
	 * @return {string}
	 */
	const getState = ( addressArray ) => {
		let state = '';
			for( let i = 0; i < addressArray.length; i++ ) {
				if ( addressArray[ i ].types[0] && addressArray[ i ].types.includes('administrative_area_level_1') ) {
					state = addressArray[ i ].long_name;
					return state;
				}
			}
    };
	
	/**
	 * Get the country
	 *
	 * @param addressArray
	 * @return {string}
	 */
	const getCountry = ( addressArray ) => {
		let country = '';
		for( let i = 0; i < addressArray.length; i++ ) {
			for( let i = 0; i < addressArray.length; i++ ) {
				if ( addressArray[ i ].types[0] && addressArray[ i ].types.includes('country') ) {
					country = addressArray[ i ].long_name;
					return country;
				}
			}
		}
    };
	/**
	 * Get the AddressLine
	 *
	 * @param addressArray
	 * @return {string}
	 */
	const getAddressLine = ( addressArray ) => {
		let addressLine = '';
		const filteredStrings = ["street_number","route"]

		for( let x = 0; x < filteredStrings.length; x++ ) {
			for( let i = 0; i < addressArray.length; i++ ) {
				if ( addressArray[ i ].types[0] && addressArray[ i ].types.includes(filteredStrings[x])) {
					addressLine += " "+addressArray[ i ].long_name;
					
				}
			}
		}
		
		return addressLine.replace(/^,/, '');
    };

    // ============================================================
    // NEW HELPER: Check if a geocode result is a non-address type
    // (establishment, neighborhood, point_of_interest, etc.)
    // ============================================================
    /**
     * Check if a geocode result is a non-address type
     * @param {Object} result - A single Google geocode result
     * @returns {boolean} true if the result is a non-address type
     */
    const isNonAddressResult = (result) => {
        if (!result || !result.types) return false;
        return result.types.some(type => NON_ADDRESS_TYPES.includes(type));
    };

    // ============================================================
    // NEW HELPER: Find the best street-level geocode result
    // from the full results array returned by Google Geocoder.
    //
    // Priority order:
    //   1. street_address type
    //   2. route type (street without house number)
    //   3. Any result that has both street_number + route components
    //   4. First result as final fallback
    // ============================================================
    /**
     * Find the best street-level result from Google geocode results array.
     * Avoids picking establishment/neighborhood/business results.
     *
     * @param {Array} results - Full array of Google geocode results
     * @returns {Object} The best street-level result
     */
    const getBestGeocodeResult = (results) => {
        if (!results || results.length === 0) return null;
        if (results.length === 1) return results[0];

        // Priority 1: Find a result with type 'street_address'
        const streetAddressResult = results.find(r => 
            r.types && r.types.includes('street_address')
        );
        if (streetAddressResult) return streetAddressResult;

        // Priority 2: Find a result with type 'route' (street-level but no house number)
        const routeResult = results.find(r => 
            r.types && r.types.includes('route') && !isNonAddressResult(r)
        );
        if (routeResult) return routeResult;

        // Priority 3: Find any result that has street_number + route in address_components
        const hasStreetComponents = results.find(r => {
            if (!r.address_components) return false;
            const hasStreetNumber = r.address_components.some(c => c.types.includes('street_number'));
            const hasRoute = r.address_components.some(c => c.types.includes('route'));
            return hasStreetNumber && hasRoute;
        });
        if (hasStreetComponents) return hasStreetComponents;

        // Priority 4: Find any result that is NOT a non-address type
        const nonEstablishment = results.find(r => !isNonAddressResult(r));
        if (nonEstablishment) return nonEstablishment;

        // Final fallback: return first result
        return results[0];
    };

    // ============================================================
    // NEW HELPER: Get a clean address line from address_components,
    // with a smart fallback that strips neighborhood/establishment
    // names from formatted_address when street_number/route are
    // not available.
    //
    // Example:
    //   formatted_address = "Moore Therapy & Rehab, 2500 S Power Rd, Mesa, AZ 85209"
    //   → returns "2500 S Power Rd"  (skips the business name)
    //
    //   formatted_address = "Power Ranch, 2500 S Power Rd, Mesa, AZ 85209"
    //   → returns "2500 S Power Rd"  (skips the neighborhood name)
    //
    //   formatted_address = "2500 S Power Rd, Mesa, AZ 85209"
    //   → returns "2500 S Power Rd"  (no business/neighborhood prefix)
    // ============================================================
    /**
     * Get a clean address line from address components with smart fallback.
     * When street_number/route extraction fails, parses formatted_address
     * and strips any leading neighborhood/establishment/business names.
     *
     * @param {Array} addressComponents - address_components from geocode result
     * @param {string} formattedAddress - formatted_address from geocode result
     * @returns {string} Clean street address line
     */
    const getCleanAddressLine = (addressComponents, formattedAddress) => {
        // First try: extract street_number + route from address_components
        const addressLine = getAddressLine(addressComponents);
        if (addressLine && addressLine.trim()) {
            return addressLine.trim();
        }

        // Second try: parse formatted_address intelligently
        if (!formattedAddress) return '';

        const segments = formattedAddress.split(',').map(s => s.trim());
        if (segments.length === 0) return '';

        // Check if the first segment looks like a street address
        // A street address typically starts with a number (e.g. "2500 S Power Rd")
        const firstSegment = segments[0];
        const startsWithNumber = /^\d+\s/.test(firstSegment);

        if (startsWithNumber) {
            // First segment is already a street address, use it
            return firstSegment;
        }

        // First segment does NOT start with a number — likely a business/neighborhood name.
        // Check the second segment.
        if (segments.length > 1) {
            const secondSegment = segments[1];
            const secondStartsWithNumber = /^\d+\s/.test(secondSegment);

            if (secondStartsWithNumber) {
                // Second segment is the real street address
                // e.g. "Moore Therapy & Rehab, 2500 S Power Rd, Mesa, AZ 85209"
                //       → "2500 S Power Rd"
                return secondSegment;
            }
        }

        // Third try: look through all address_components for any route name
        // even without a street number (e.g. just "S Power Rd")
        if (addressComponents && addressComponents.length > 0) {
            const route = addressComponents.find(comp => 
                comp.types && comp.types.includes('route')
            );
            if (route) {
                return route.long_name;
            }
        }

        // Final fallback: use first segment as-is (last resort)
        return firstSegment;
    };

	const MapServices ={
        getArea,
        getCity,
        getState,
		getPostalCode,
		getCountry,
		getAddressLine,
        getBestGeocodeResult,
        getCleanAddressLine,
        isNonAddressResult
    }
    
    export default MapServices